
import {
  FinanceLoanClass,
  FinanceQuoteFee,
  FinanceQuoteFeeWithDescription,
  GenericGrid
} from '@amsanalytics/react-shared-library';
import React from 'react';
import styles from './_finance-fees-editor.module.scss';
import { hasValue } from "@amsanalytics/react-shared-library";
import {TileLayout, TileLayoutItem, TilePosition} from "@progress/kendo-react-layout";
import {IConfiguration} from "../../../../Data/ConfigurationData";
import {IApplication} from "../../../../Interfaces/Application/IApplication";
import {IRuleViolation} from "../../../../Interfaces/Application/IApplicationQuote";
import {getFinanceQuoteFeesThatAreDisplayed} from "../../../../utils/quoteUtils";
import GenericItemEditorDialog, {
  GenericEditorActionType,
  IGenericItemEditorError,
  IGenericItemFieldInfo
} from "../../GenericItemEditorDialog/GenericItemEditorDialog";
import {ILookupItems} from "../../../../Interfaces/ILookupItems";

export interface FinanceFeesEditorProps {
  quoteFees: FinanceQuoteFee[];
  loanClass: FinanceLoanClass | undefined;
  onChange: (fee: FinanceQuoteFee) => void;
  configuration: IConfiguration;
  financeQuoteFieldIsDisabled: (fieldName: string) => boolean;
  financeLoanClasses: FinanceLoanClass[];
  currentApp: IApplication;
  applicationFinanceErrors: IRuleViolation[];
  lookups: ILookupItems;
  // Dialog functions (passed from parent)
  openDialog: (options: { children: React.ReactNode }) => void;
  closeDialog: () => void;
}

export default function FinanceFeesEditor(props: FinanceFeesEditorProps) {
  const { openDialog, closeDialog } = props;
  // Hide the quote fees that are supposed to be hidden from the Fees tab
  const quoteFeesToDisplay = getFinanceQuoteFeesThatAreDisplayed(props.quoteFees, props.loanClass?.financeLoanClassFinanceFees);

  const editableFieldInfos: IGenericItemFieldInfo[] = [
    {name: "description", label: "Description", type: "string", required: true, readonly: true},
    {name: "amount", label: "Amount", type: "number", required: true, readonly: false}
  ];

  const validateFee = (fee: FinanceQuoteFee, actionType: GenericEditorActionType): IGenericItemEditorError[] => {
    const errors: IGenericItemEditorError[] = [];

    if (hasValue(fee.amount) && fee.amount! < 0) {
      errors.push({message: "The amount can't be less than 0", canBeBypassed: false});
    }

    return errors;
  }

  const editFee = (fee: FinanceQuoteFee) => {
    const buttonText = "Update";

    openDialog({
      children: (
        <GenericItemEditorDialog
          item={fee as FinanceQuoteFee}
          objectSpecificValidator={validateFee}
          title={"Fee"}
          editableFieldInfos={editableFieldInfos}
          submitButtonText={buttonText}
          onSubmit={(updatedFee: FinanceQuoteFee) => props.onChange(updatedFee)}
          closeDialog={closeDialog}
        />
      )
    })
  }

  const [data, setData] = React.useState<Array<TilePosition>>([
    { col: 1, colSpan: 12, rowSpan: 2, row: 1 }
  ]);

  const tiles: TileLayoutItem[] = [
    {
      defaultPosition: data[0],
      reorderable: false,
      resizable: false,
      header: 'Fees',
      body: <div className={styles.financeFeesEditorContainer}>
        <GenericGrid<FinanceQuoteFeeWithDescription>
          style={{display: 'flex', flex: 1}}
          columns={[
            {title: "Description", field: "description"},
            {title: "Amount", field: "amount", format: '{0:c}'},
          ]}
          itemName={'Fee'}
          items={quoteFeesToDisplay.sort((a, b) =>
            a.description > b.description ? 1 : -1
          ) ?? []}
          disableAdding={true}
          disableEditing={props.financeQuoteFieldIsDisabled("fees")}
          disableExcelExporting={true}
          disablePdfExporting={true}
          disableFiltering={true}
          disableGrouping={true}
          onItemSelected={(fee: FinanceQuoteFee) => { if (!props.financeQuoteFieldIsDisabled("fees")) editFee(fee); }}
          disablePaging={quoteFeesToDisplay.length <= 20}
        />
      </div>,
    }
  ];
  
  return (
    <TileLayout
      columns={12}
      className={styles.quoteEditorTileLayout}
      gap={{ rows: 10, columns: 10 }}
      rowHeight={"100%"}
      // style={{display: 'flex', flex: 1}}
      style={{ width: '100%'}}
      positions={data}
      items={tiles}
    />
  );
}
